import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDemo } from "@/store/DemoContext";
import { getFixtureById } from "@/data";
import { toast } from "sonner";
import { SlidersHorizontal, X, RotateCcw, Sparkles } from "lucide-react";

function Row({ label, children }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-display">{label}</div>
      <div className="flex flex-wrap gap-1">{children}</div>
    </div>
  );
}

function Seg({ active, onClick, children, testid, accent = "#b6ff2e" }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors"
      style={{
        background: active ? accent : "transparent",
        color: active ? "#0a0a0a" : "#cfcfcf",
        borderColor: active ? accent : "#2b2b2b",
      }}>
      {children}
    </button>
  );
}

const STATES = ["OPEN", "LOCKED", "REVEALED", "IN_PLAY", "SETTLED"];
const STATE_KEYS = { OPEN: "demo.open", LOCKED: "demo.locked", REVEALED: "demo.revealed", IN_PLAY: "demo.inPlay", SETTLED: "demo.settled" };

export function DemoPanel() {
  const { t, i18n } = useTranslation();
  const { state, set, setFixtureState, getState, reset, activeFixtureId } = useDemo();
  const [open, setOpen] = useState(false);

  const fixture = getFixtureById(activeFixtureId);
  const homeCode = fixture ? fixture.homeId.split("-")[1] : "";
  const awayCode = fixture ? fixture.awayId.split("-")[1] : "";
  const curState = fixture ? getState(fixture) : "OPEN";

  const changeLang = (lng) => { set("language", lng); i18n.changeLanguage(lng); };

  if (!open) {
    return (
      <button data-testid="demo-panel-toggle" onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-[60] flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-neutral-900 border border-neutral-700 text-neutral-200 shadow-2xl hover:border-[#b6ff2e]/60 transition-colors">
        <SlidersHorizontal size={16} className="text-[#b6ff2e]" />
        <span className="text-xs font-display font-bold uppercase tracking-wider">Demo</span>
      </button>
    );
  }

  return (
    <div data-testid="demo-panel"
      className="fixed bottom-4 right-4 z-[60] w-[300px] max-w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto rounded-2xl bg-neutral-950/95 backdrop-blur-md border border-neutral-700 shadow-2xl">
      <div className="sticky top-0 flex items-center justify-between px-3.5 py-2.5 border-b border-neutral-800 bg-neutral-950/95 backdrop-blur">
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={15} className="text-[#b6ff2e]" />
          <div>
            <div className="text-xs font-display font-bold uppercase tracking-wider text-neutral-100">{t("demo.title")}</div>
            <div className="text-[9px] text-neutral-500">{t("demo.subtitle")}</div>
          </div>
        </div>
        <button data-testid="demo-panel-close" onClick={() => setOpen(false)} className="text-neutral-500 hover:text-neutral-200">
          <X size={16} />
        </button>
      </div>

      <div className="p-3.5 space-y-3.5">
        <Row label={`${t("demo.fixtureState")} · ${homeCode}–${awayCode}`}>
          {STATES.map((s) => (
            <Seg key={s} testid={`demo-state-${s}`} active={curState === s}
              onClick={() => setFixtureState(fixture.id, s)}>{t(STATE_KEYS[s])}</Seg>
          ))}
        </Row>

        <Row label={t("demo.countdownSpeed")}>
          <Seg testid="demo-speed-real" active={state.countdownSpeed === "real"} onClick={() => set("countdownSpeed", "real")}>{t("demo.realTime")}</Seg>
          <Seg testid="demo-speed-60x" active={state.countdownSpeed === "60x"} onClick={() => set("countdownSpeed", "60x")}>{t("demo.faster")}</Seg>
          <Seg testid="demo-speed-freeze" active={state.countdownSpeed === "freeze"} onClick={() => set("countdownSpeed", "freeze")}>{t("demo.freeze")}</Seg>
        </Row>

        <Row label={t("demo.userProfile")}>
          <Seg testid="demo-profile-new" active={state.profileId === "new"} onClick={() => set("profileId", "new")}>{t("demo.newUser")}</Seg>
          <Seg testid="demo-profile-mid" active={state.profileId === "mid"} onClick={() => set("profileId", "mid")}>{t("demo.midUser")}</Seg>
          <Seg testid="demo-profile-elite" active={state.profileId === "elite"} onClick={() => set("profileId", "elite")}>{t("demo.eliteUser")}</Seg>
        </Row>

        <Row label={t("demo.resultVariant")}>
          <Seg testid="demo-variant-A" active={state.resultVariant === "A"} onClick={() => set("resultVariant", "A")}>{t("demo.variantA")}</Seg>
          <Seg testid="demo-variant-B" active={state.resultVariant === "B"} onClick={() => set("resultVariant", "B")}>{t("demo.variantB")}</Seg>
        </Row>

        <Row label={t("demo.scoringMode")}>
          <Seg testid="demo-mode-A" accent="#3fd7e5" active={state.scoringMode === "A"} onClick={() => set("scoringMode", "A")}>{t("demo.modeA")}</Seg>
          <Seg testid="demo-mode-B" accent="#3fd7e5" active={state.scoringMode === "B"} onClick={() => set("scoringMode", "B")}>{t("demo.modeB")}</Seg>
          <Seg testid="demo-mode-C" accent="#3fd7e5" active={state.scoringMode === "C"} onClick={() => set("scoringMode", "C")}>{t("demo.modeC")}</Seg>
        </Row>

        <Row label={t("demo.language")}>
          <Seg testid="demo-lang-en" accent="#ffb547" active={state.language === "en"} onClick={() => changeLang("en")}>English</Seg>
          <Seg testid="demo-lang-de" accent="#ffb547" active={state.language === "de"} onClick={() => changeLang("de")}>Deutsch</Seg>
          <Seg testid="demo-lang-es" accent="#ffb547" active={state.language === "es"} onClick={() => changeLang("es")}>Español</Seg>
        </Row>

        <button data-testid="demo-reset"
          onClick={() => { reset(); i18n.changeLanguage("en"); toast.success(t("demo.resetConfirm")); }}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs font-medium transition-colors">
          <RotateCcw size={14} /> {t("demo.reset")}
        </button>
        <div className="flex items-center gap-1.5 text-[9px] text-neutral-600">
          <Sparkles size={10} /> Persisted to localStorage · survives refresh
        </div>
      </div>
    </div>
  );
}
